import React, { useState } from "react";
import { Button, Group, Stack, TextInput } from "@mantine/core";
import BottomSheet from "../../../common/BottomSheet";
import { DESCRIPTION_MAX_LENGTH } from "../../shell/ReportHeader";

export interface MobileMetaSheetProps {
  opened: boolean;
  onClose: () => void;
  name: string;
  description: string;
  /** Catalog name, shown as the name placeholder. */
  defaultName: string;
  /** Persist the edit. Resolve true on success (the sheet then closes). */
  onSave: (name: string, description: string) => Promise<boolean>;
}

type FormProps = Omit<MobileMetaSheetProps, "opened">;

/** Mounted per open, so it always starts from the report's current name
 *  and description. */
function MetaForm({
  onClose,
  name,
  description,
  defaultName,
  onSave,
}: FormProps) {
  const [draftName, setDraftName] = useState(name);
  const [draftDescription, setDraftDescription] = useState(description);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (await onSave(draftName, draftDescription)) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="sm">
      <TextInput
        label="Name"
        value={draftName}
        onChange={(e) => setDraftName(e.currentTarget.value)}
        placeholder={defaultName || "Report name"}
      />
      <TextInput
        label="Description"
        value={draftDescription}
        onChange={(e) => setDraftDescription(e.currentTarget.value)}
        maxLength={DESCRIPTION_MAX_LENGTH}
        placeholder="e.g. Last month · front desk staff"
      />
      <Group justify="flex-end" gap="xs">
        <Button variant="default" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="teal" onClick={() => void save()} loading={saving}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}

/** "Name & description" for a report on a phone (ReportHeader's inline
 *  editor on desktop). */
export default function MobileMetaSheet({
  opened,
  ...form
}: MobileMetaSheetProps) {
  return (
    <BottomSheet
      labelDialog
      opened={opened}
      onClose={form.onClose}
      title="Name & description"
      data-testid="report-meta-sheet"
    >
      {opened && <MetaForm {...form} />}
    </BottomSheet>
  );
}
